import {
  Creative,
  CreativeApprovalConfig,
  CreativeBindingCampaign,
  CreativeListRecord,
  CreativeOfCampaign,
  CreativeSize,
  CreativeStatusFilter,
  CreativeType,
  creativeTypeOrder,
  isCombo,
  isVideo
} from './Creative';
import {
  CreativesWithPagination,
  CreativeWebService,
  RestfulCreativeWebService,
  WaitingBindGetParams
} from 'ws/CreativeWebService';
import { Pageable } from 'ws/Pageable';
import {
  BindingWebService,
  RestfulBindingWebService
} from 'ws/BindingWebService';
import { compact, isEmpty, omitBy } from 'lodash';
import i18n from 'i18n';
import { getLimitationContentLabel } from 'utils/LimitationUtil';
import { AddonFeatureManager } from 'core/auth/AddonFeatureManager';
import { AddonFeatureType } from 'core/agency/AddonFeature';
import {
  ChannelGroupSetting,
  CreativeTypeSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { SummaryData } from 'components/SummaryDetail/SummaryDetail';

export interface CreativeManager {
  getCreativesByCampaignId(
    campaignId,
    isDraft: boolean
  ): Promise<CreativeOfCampaign[]>;
  getWatingBindCreatives(
    payload: WaitingBindGetParams
  ): Promise<CreativeOfCampaign[]>;
  reviewCreatives(creativeIds: number[], adxs: Array<string>): Promise<void>;
  getCreative(creativeId: number): Promise<Creative>;
  getCreatives(
    creativeType: CreativeType,
    advertiserId: number | undefined,
    pageable: Pageable,
    search?: string,
    status?: CreativeStatusFilter
  ): Promise<CreativesWithPagination>;
  createCreative(creative: FormData): Promise<{
    creativeId: number;
  }>;
  createCreativesByJson(creatives: any): Promise<
    | {
        data: any[];
        error: {
          error_message: string | null;
          name: string;
        }[];
      }
    | undefined
  >;
  createCreativeAndBind(
    channel: string,
    channelGroupId: number | undefined,
    creative: FormData,
    campaignId: string,
    isDraft: boolean
  ): Promise<void>;
  createCreativesByJsonAndBind(
    channel: string,
    channelGroupId: number | undefined,
    creatives: any,
    campaignId: string,
    isDraft: boolean
  ): Promise<void>;
  updateCreative(creative: FormData): Promise<number>;
  updateCreativeBindingStatus(creatives: number[], enable: boolean);
  cloneCreatives(creatives: number[]): Promise<void>;
  deleteCreative(creativeIds: number[]): Promise<void>;
  updateCreativeBindingCampaigns(
    creativeId: number,
    campaigns: number[],
    isActive: boolean
  );
  getCreativeOptions(from?: string, to?: string): Promise<Array<SelectOptions>>;
  getCreativeSizeOptions(): Promise<Array<SelectOptions>>;
  getPpsLayoutId(): Promise<SelectOptions[]>;
  getPpsThumbnail(creativeId): Promise<string[]>;
  getCreativeNativeToBannerTemplate(): Promise<any>;
  getBindingOptions(): Promise<SelectOptions[]>;
  generateRectImgBySquareImg(imageUrl: string): Promise<string>;
  getAdneonCreativePreview(data: any): Promise<any>;
  publicVideoAssetId(
    videoAssetId: string,
    creativeType: CreativeType
  ): Promise<string>;
  getCreativeTypeOptions(): {
    label: string;
    value: number;
  }[];
  getCreativeTypeLabel(creativeType: number): string;
  getLimitationSummaryData(limitationData: any);
  getCreativeSizesByAdFormat(adFormat: string): Promise<CreativeSize[]>;
  checkCreativeSupportSchedule(creativeId: number): Promise<boolean>;
  isCreativeProcessing(creative: CreativeListRecord): boolean;
  isCreativeTypeSupport(
    creativeType: number,
    addonFeatureManager: AddonFeatureManager
  ): boolean;
  getCreativeTypeSetting(creativeType: number): CreativeTypeSetting;
  getCreativeApprovalConfigs(): Promise<CreativeApprovalConfig[]>;
  getCreativeBindingStatus(
    creativeId: number
  ): Promise<CreativeBindingCampaign[]>;
}

export class DefaultCreativeManager implements CreativeManager {
  webService: CreativeWebService;
  creativeTypeSettings: Map<number, CreativeTypeSetting>;

  constructor(
    webService: CreativeWebService = new RestfulCreativeWebService(),
    private bindingWebService: BindingWebService = new RestfulBindingWebService()
  ) {
    this.webService = webService;
    this.creativeTypeSettings = ChannelGroupSetting.creativeTypeSettings(true);
  }

  async getCreativesByCampaignId(
    campaignId,
    isDraft: boolean
  ): Promise<CreativeOfCampaign[]> {
    return this.webService.getCreativesByCampaignId(campaignId, isDraft);
  }

  async getWatingBindCreatives(
    payload: WaitingBindGetParams
  ): Promise<CreativeOfCampaign[]> {
    return this.webService.getWatingBindCreatives(payload);
  }

  async reviewCreatives(
    creativeIds: number[],
    adxs: Array<string>
  ): Promise<void> {
    return this.webService.reviewCreatives(creativeIds, adxs);
  }

  async getCreative(creativeId: number): Promise<Creative> {
    return this.webService.getCreative(creativeId);
  }

  async getCreatives(
    creativeType: CreativeType,
    advertiserId: number | undefined,
    pageable: Pageable,
    search?: string,
    status?: CreativeStatusFilter
  ): Promise<CreativesWithPagination> {
    return this.webService.getCreatives(
      creativeType,
      advertiserId,
      pageable,
      search,
      status
    );
  }

  async createCreative(creative: FormData): Promise<{
    creativeId: number;
  }> {
    return this.webService.createCreative(creative);
  }

  async createCreativesByJson(creatives: any): Promise<
    | {
        data: any[];
        error: {
          error_message: string | null;
          name: string;
        }[];
      }
    | undefined
  > {
    return this.webService.createCreativesByJson(creatives);
  }

  async updateCreative(creative: FormData): Promise<number> {
    return this.webService.updateCreative(creative);
  }

  async createCreativeAndBind(
    channel: string,
    channelGroupId: number | undefined,
    creative: FormData,
    adSetId: string,
    isDraft: boolean
  ): Promise<void> {
    const { creativeId } = await this.webService.createCreative(creative);
    return this.bindingWebService.createBinding(channel, channelGroupId, [
      {
        l2ChannelIds: [adSetId],
        goCreativeIds: [creativeId.toString()],
        active: true,
        draft: isDraft
      }
    ]);
  }

  async createCreativesByJsonAndBind(
    channel: string,
    channelGroupId: number | undefined,
    creatives: any,
    campaignId: string,
    isDraft: boolean
  ): Promise<void> {
    const data = await this.webService.createCreativesByJson(creatives);
    if (!data) {
      return;
    }
    const creativeIds = data.data.map((newCreative) =>
      newCreative.creative.creativeId.toString()
    );
    return this.bindingWebService.createBinding(channel, channelGroupId, [
      {
        l2ChannelIds: [campaignId],
        goCreativeIds: creativeIds,
        active: true,
        draft: isDraft
      }
    ]);
  }

  async updateCreativeBindingStatus(creatives: number[], enable: boolean) {
    return this.webService.updateCreativeBindingStatus(creatives, enable);
  }

  async cloneCreatives(creatives: number[]): Promise<void> {
    return this.webService.cloneCreatives(creatives);
  }

  async deleteCreative(creativeIds: number[]): Promise<void> {
    return this.webService.deleteCreative(creativeIds);
  }

  async updateCreativeBindingCampaigns(
    creativeId: number,
    campaigns: number[],
    isActive: boolean
  ) {
    return this.webService.updateCreativeBindingCampaigns(
      creativeId,
      campaigns,
      isActive
    );
  }

  async getCreativeOptions(from?: string, to?: string) {
    return this.webService.getCreativeOptions(from, to);
  }

  async getCreativeSizeOptions() {
    return this.webService.getCreativeSizeOptions();
  }

  async getPpsLayoutId() {
    return this.webService.getPpsLayoutId();
  }

  async getPpsThumbnail(creativeId) {
    return this.webService.getPpsThumbnail(creativeId);
  }

  async getCreativeNativeToBannerTemplate() {
    return this.webService.getCreativeNativeToBannerTemplate();
  }

  async getBindingOptions(): Promise<SelectOptions[]> {
    return this.webService.getBindingOptions();
  }

  async generateRectImgBySquareImg(imageUrl: string): Promise<string> {
    return this.webService.generateRectImgBySquareImg(imageUrl);
  }

  async getAdneonCreativePreview(data: any): Promise<any> {
    return this.webService.getAdneonCreativePreview(data);
  }

  async publicVideoAssetId(
    videoAssetId: string,
    creativeType: CreativeType
  ): Promise<string> {
    return this.webService.publicVideoAssetId(videoAssetId, creativeType);
  }

  getCreativeTypeLabel(creativeType: number): string {
    return this.creativeTypeSettings.has(creativeType)
      ? this.creativeTypeSettings.get(creativeType)!.name
      : 'unknown';
  }

  getCreativeTypeOptions(): {
    label: string;
    value: number;
  }[] {
    const types = Array.from(this.creativeTypeSettings.keys());
    return [
      ...types.sort((a, b) => {
        if (creativeTypeOrder.indexOf(a) === -1) {
          return 1;
        }
        if (creativeTypeOrder.indexOf(b) === -1) {
          return -1;
        }
        return creativeTypeOrder.indexOf(a) - creativeTypeOrder.indexOf(b);
      })
    ].map((creativeType) => {
      const setting = this.getCreativeTypeSetting(creativeType);
      return {
        label: setting.name,
        value: setting.bannerTypeId
      };
    });
  }

  getLimitationSummaryData(limitationData: any): {
    [key: string]: SummaryData[];
  } {
    const getLimitationContent = (limitationData): SummaryData[] => {
      if (!limitationData) {
        return [];
      }
      return compact(
        limitationData.map((data) => {
          const ignoreType = ['unicornlanguage'];
          if (ignoreType.includes(data.type)) {
            return undefined;
          }

          if (!data.value || data.value.length === 0) {
            return undefined;
          }

          let labelI18nKey = `limitation.labels.${data.type}`;
          if (!i18n.exists(labelI18nKey)) {
            labelI18nKey = `productGroup.space.attributes.titles.${data.type}`;
          }

          let label = i18n.exists(labelI18nKey)
            ? i18n.t(labelI18nKey)
            : data.type;
          // value 維持逗號串給既有呼叫端；明細改成逐筆換行，另附未 join 的陣列。
          const values = data.value.map((value) =>
            getLimitationContentLabel(data.type, value)
          );
          return {
            label,
            value: values.join(', '),
            values
          };
        })
      );
    };

    const result = {
      include: getLimitationContent(limitationData.include),
      preferred: getLimitationContent(limitationData.preferred),
      nonPreferred: getLimitationContent(limitationData.nonPreferred),
      exclude: getLimitationContent(limitationData.exclude)
    };

    if (!limitationData.other) {
      return omitBy(result, isEmpty);
    }

    return omitBy(
      {
        ...result,
        other: getLimitationContent(limitationData.other)
      },
      isEmpty
    );
  }

  async getCreativeSizesByAdFormat(adFormat: string) {
    return this.webService.getCreativeSizesByAdFormat(adFormat);
  }

  async checkCreativeSupportSchedule(creativeId: number): Promise<boolean> {
    return this.webService.checkCreativeSupportSchedule(creativeId);
  }

  isCreativeProcessing(creative: CreativeListRecord): boolean {
    if (isVideo(creative.creativeType)) {
      return creative.videoChildren &&
        Object.keys(creative.videoChildren).length > 0
        ? Object.values(creative.videoChildren).some((child) => !child.videoUrl)
        : false;
    }
    if (isCombo(creative.creativeType)) {
      return !creative.creativeValues.videoUrl;
    }
    return false;
  }

  isCreativeTypeSupport(
    creativeType: number,
    addonFeatureManager: AddonFeatureManager
  ): boolean {
    const target = `option_${creativeType}`;
    const creativeTypeSetting = this.creativeTypeSettings.get(creativeType);
    if (!creativeTypeSetting) {
      return false;
    }

    const channelGaroupSetting = ChannelGroupSetting.fromId(
      creativeTypeSetting.channelGroupId
    );
    if (!channelGaroupSetting) {
      return false;
    }

    return addonFeatureManager.isEveryFeaturesEnable(
      target as AddonFeatureType,
      channelGaroupSetting.config.addonName as AddonFeatureType
    );
  }

  getCreativeTypeSetting(creativeType: number) {
    const setting = this.creativeTypeSettings.get(creativeType);
    if (!setting) {
      throw new Error(
        `Creative type setting not found for creative type: ${creativeType}`
      );
    }
    return setting;
  }

  async getCreativeApprovalConfigs(): Promise<CreativeApprovalConfig[]> {
    return this.webService.getCreativeApprovalConfigs();
  }

  async getCreativeBindingStatus(
    creativeId: number
  ): Promise<CreativeBindingCampaign[]> {
    return this.webService.getCreativeBindingStatus(creativeId);
  }
}
